import XUILoader from '@xero/xui/react/loader';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHeaderData } from './actions';
import './App.css';
import { removeHeader, setupHeader } from './helpers/header';
import { Pages } from './helpers/routes';
import { FetchError } from './models';
import Fatality from './pages/Fatality';
import { bootstrapSelectors, getBootstrapAction } from './redux/slices/BootstrapSlice';
import { installationSelectors } from './redux/slices/InstallationSlice';
import { organisationSelectors } from './redux/slices/OrganisationSlice';
import { userSelectors } from './redux/slices/UserSlice';
import { useAppDispatch } from './redux/store';
import { LDClient } from 'launchdarkly-js-client-sdk';
import ErrorComponent from './components/ErrorComponent/ErrorComponent';
import Link from './components/Link';
import React from 'react';
import lang from './static/lang/en-NZ';

export interface IAppProps {
    ldClient?: LDClient;
}

const App: FC<IAppProps> = (props) => {
    const dispatch = useAppDispatch();

    const isAllowed = useSelector(bootstrapSelectors.isAllowed);
    const isBootstrapping = useSelector(bootstrapSelectors.isBootstrapping);
    const bootstrapError = useSelector(bootstrapSelectors.error);
    const isFetching = useSelector(installationSelectors.isFetching);
    const installation = useSelector(installationSelectors.installation);
    const routes = useSelector(installationSelectors.routes);
    const user = useSelector(userSelectors.user);
    const organisation = useSelector(organisationSelectors.organisation);

    const ldClient = props.ldClient!; // this should be defined by the call to withLDConsumer which wraps this component
    const loadedInitHeader = useRef(false);
    const [backendError, setBackendError] = useState<Error | undefined>();

    const initHeaderAsync = useCallback(async () => {
        try {
            const header = await getHeaderData(user);
            setupHeader(header);
        } catch (error) {
            // If we failed to get the header, don't crash the app
        }
    }, [user]);

    useEffect(() => {
        if (loadedInitHeader.current) {
            return;
        }

        loadedInitHeader.current = true;

        (async () => {
            dispatch(getBootstrapAction());
            await initHeaderAsync();
        })();
    }, [dispatch, initHeaderAsync]);

    useEffect(() => {
        setBackendError(bootstrapError);
    }, [bootstrapError]);

    useEffect(() => {
        (async () =>
            ldClient.identify({
                key: user.xero_userid,
                custom: { tenantId: organisation.id, tenantRegion: organisation.region! },
            }))();
    }, [organisation, ldClient, user.xero_userid]);

    const onConnectClick = (authorizeUrl?: string) => {
        if (authorizeUrl) {
            window.location.href = authorizeUrl;
        }
    };

    const onPaidConnectClick = () => {
        window.location.href = installation.billing?.url ?? '';
    };

    const renderPage = () => {
        const Route = routes!.get(Pages.Access)!;
        return <Route onConnectClick={onConnectClick} onPaidConnectClick={onPaidConnectClick} showDisconnectedBanner={false} />;
    };

    const onGoBack = useCallback(() => {
        window.location.reload();
    }, []);

    const goBackButton = React.useMemo(
        () => ({
            onClick: onGoBack,
            text: lang.ERROR.GO_BACK,
        }),
        [onGoBack]
    );

    const renderError = () => {
        return (
            <ErrorComponent
                description={
                    <div>
                        {lang.ERROR.GO_BACK_WAIT}
                        <Link displayLinkIcon={true} href={lang.ERROR.FIND_AN_ANSWER_LINK}>
                            {lang.ERROR.FIND_AN_ANSWER}
                        </Link>
                    </div>
                }
                heading={lang.ERROR.SOMETHING_WENT_WRONG}
                primaryButton={goBackButton}
            />
        );
    };

    const renderAppOrError = () => {
        if (backendError) {
            const error = backendError as FetchError;
            if (error.code && error.code >= 500 && error.code < 600) {
                return renderError();
            }
        }
        return renderApp();
    };

    const renderApp = () => {
        if (isBootstrapping || isFetching) {
            return <XUILoader ariaLabel='Loading' />;
        }
        if (!isAllowed) {
            // Shouldn't be here, either not a xero user or not listed on feature service
            removeHeader();
            return <Fatality header={lang.ERROR.FATAL} sub={lang.ERROR.SUB} />;
        }
        return <div className='app'>{renderPage()}</div>;
    };

    return renderAppOrError();
};

export default withLDConsumer()(App);
